// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Time and client" for agent chats (design/agents-side-bar r2-t1..t3; API agents/AgentsTime).
// Per chat: how long the agent worked, who it is for, the client's week and what was logged. The
// side bar reads what Konvo keeps (cheap) for its chips and the ClickUp task button; the panel
// asks the machine and the task. Logging to ClickUp only ever follows the person's press.
// A chat can carry several tasks (design/agents-task-linking): changeTasks links, unlinks and moves
// turns (nothing reaches ClickUp), and the Turns view reads each prompt's first line live.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {RestResponse} from '@app/features/platform/types/TransportTypes';
import type {
	AgentEstimateKnobs,
	AgentEstimateKnobsResponse,
	AgentTimeClient,
	AgentTimeClientOption,
	AgentTimeClientOptions,
	AgentTimeLogRequest,
	AgentTimePrompts,
	AgentTimeTasksRequest,
	AgentTimeView,
} from '@konvo/schema/src/domains/konvo/AgentTime';
import {makeAutoObservable, observable, runInAction} from 'mobx';

/** Silent requests resolve on 4xx/5xx with the error body: fail instead (as Agents.ts does). */
function answered<R extends RestResponse<unknown>>(response: R, method: 'GET' | 'PUT' | 'POST' = 'GET'): R {
	if (!response.ok) throw new HttpError({method, path: '', status: response.status, body: response.body});
	return response;
}

/** The person's own time zone, so days and weeks are theirs. */
export function timeZone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	} catch {
		return 'UTC';
	}
}

/** The light read (no machine) is repeated at most this often per chat. */
const LIGHT_EVERY_MS = 60_000;

export interface ChatTime {
	view: AgentTimeView | null;
	/** A full read (machine and task) is on its way. */
	loading: boolean;
	error: unknown;
	/** When the last full read came back. */
	computedAt: number | null;
	lightAt: number | null;
}

const key = (machineId: string, threadId: string) => `${machineId}\u0000${threadId}`;

/** A view as this app reads it: the lists an older API leaves out are empty. */
function withDefaults(view: AgentTimeView): AgentTimeView {
	const partial = view as Partial<AgentTimeView> & AgentTimeView;
	return {...view, tasks: partial.tasks ?? [], turnTasks: partial.turnTasks ?? [], suggest: partial.suggest ?? null};
}

class AgentTimeStore {
	readonly chats = observable.map<string, ChatTime>();
	/** The first line of each prompt (turn id → line), read live for the Turns view; never kept. */
	readonly prompts = observable.map<string, Record<string, string>>();
	options: Array<AgentTimeClientOption> | null = null;
	optionsLoading = false;

	constructor() {
		makeAutoObservable(this, {chats: false, prompts: false});
	}

	of(machineId: string, threadId: string): ChatTime | null {
		return this.chats.get(key(machineId, threadId)) ?? null;
	}

	private put(machineId: string, threadId: string, patch: Partial<ChatTime>): void {
		// An API from before several tasks has no task list, turns or suggestions: the chat's one task.
		if (patch.view) patch = {...patch, view: withDefaults(patch.view)};
		const current = this.chats.get(key(machineId, threadId)) ?? {
			view: null,
			loading: false,
			error: null,
			computedAt: null,
			lightAt: null,
		};
		this.chats.set(key(machineId, threadId), {...current, ...patch});
	}

	/** What Konvo keeps for the chat, without asking the machine: for the side bar. */
	async loadKept(machineId: string, threadId: string): Promise<void> {
		const current = this.of(machineId, threadId);
		if (current && (current.loading || (current.lightAt && Date.now() - current.lightAt < LIGHT_EVERY_MS))) return;
		this.put(machineId, threadId, {lightAt: Date.now()});
		try {
			const response = answered(
				await http.get<AgentTimeView>(Endpoints.KONVO_AGENTS_THREAD_TIME(machineId, threadId), {
					query: {tz: timeZone()},
					mode: 'silent',
				}),
			);
			runInAction(() => {
				// A full read that landed meanwhile is newer.
				if (!this.of(machineId, threadId)?.computedAt) this.put(machineId, threadId, {view: response.body});
			});
		} catch {
			// The chips go without; the panel reads again.
		}
	}

	/** The chat's time read from the machine and its task (the panel). */
	async load(machineId: string, threadId: string, force = false): Promise<void> {
		this.put(machineId, threadId, {loading: true, error: null});
		try {
			const response = answered(
				await http.get<AgentTimeView>(Endpoints.KONVO_AGENTS_THREAD_TIME(machineId, threadId), {
					query: {tz: timeZone(), compute: '1', ...(force ? {force: '1'} : {})},
					mode: 'silent',
				}),
			);
			runInAction(() =>
				this.put(machineId, threadId, {
					view: response.body,
					loading: false,
					computedAt: Date.now(),
					lightAt: Date.now(),
				}),
			);
		} catch (error) {
			runInAction(() => this.put(machineId, threadId, {loading: false, error}));
		}
	}

	private async write(
		machineId: string,
		threadId: string,
		method: 'put' | 'post',
		path: string,
		body: unknown,
	): Promise<AgentTimeView> {
		const url = `${Endpoints.KONVO_AGENTS_THREAD_TIME(machineId, threadId)}${path}`;
		const response = answered(
			method === 'put'
				? await http.put<AgentTimeView>(url, {body, mode: 'silent'})
				: await http.post<AgentTimeView>(url, {body, mode: 'silent'}),
			method === 'put' ? 'PUT' : 'POST',
		);
		runInAction(() => this.put(machineId, threadId, {view: response.body, error: null}));
		return response.body;
	}

	/** The client for this chat, or for every chat in its project; null clears that pick. */
	async setClient(
		machineId: string,
		threadId: string,
		client: AgentTimeClient | null,
		scope: 'chat' | 'project',
	): Promise<void> {
		await this.write(machineId, threadId, 'put', '/client', {client, scope, tz: timeZone()});
		runInAction(() => {
			this.options = null;
		});
	}

	/** Links a ClickUp task (null unlinks a picked one). */
	async setTask(machineId: string, threadId: string, taskId: string | null): Promise<void> {
		await this.write(machineId, threadId, 'put', '/task', {taskId, tz: timeZone()});
	}

	/**
	 * Changes the chat's tasks: link (from a turn on), unlink ("Not this one"), move a turn (or it and
	 * all after it), link both of Konvo's suggestions split by turn, or "None of these".
	 */
	async changeTasks(machineId: string, threadId: string, body: AgentTimeTasksRequest): Promise<AgentTimeView> {
		return this.write(machineId, threadId, 'put', '/tasks', {...body, tz: timeZone()});
	}

	promptsOf(machineId: string, threadId: string): Record<string, string> | null {
		return this.prompts.get(key(machineId, threadId)) ?? null;
	}

	/** Each prompt's first line, read live from the machine (the Turns view); kept only in memory. */
	async loadPrompts(machineId: string, threadId: string): Promise<void> {
		try {
			const response = answered(
				await http.get<AgentTimePrompts>(`${Endpoints.KONVO_AGENTS_THREAD_TIME(machineId, threadId)}/prompts`, {
					mode: 'silent',
				}),
			);
			runInAction(() => this.prompts.set(key(machineId, threadId), response.body.prompts));
		} catch {
			// The Turns view shows the lines the thread on screen has, else the turn's number.
		}
	}

	/** What Konvo keeps for the chat, read again (while Konvo is looking for its task). */
	async reread(machineId: string, threadId: string): Promise<void> {
		try {
			await this.refresh(machineId, threadId);
		} catch {
			// The next look tries again.
		}
	}

	/**
	 * Logs what the person ticked, up to `upTo` (when they confirmed): the agent time not logged yet
	 * (as measured, or their minutes) and their own minutes.
	 */
	async log(
		machineId: string,
		threadId: string,
		upTo: string,
		entries: Pick<AgentTimeLogRequest, 'agent' | 'you' | 'tasks'> = {},
	): Promise<AgentTimeView> {
		return this.write(machineId, threadId, 'post', '/log', {upTo, tz: timeZone(), ...entries});
	}

	/** The person's own minutes on the chat; null goes back to the estimate. */
	async setYou(machineId: string, threadId: string, minutes: number | null): Promise<void> {
		await this.write(machineId, threadId, 'put', '/you', {minutes, tz: timeZone()});
	}

	/**
	 * Tweaks the estimate's knobs (null: the defaults) for every chat, then reads the open chat again
	 * so its estimate follows.
	 */
	async setKnobs(knobs: AgentEstimateKnobs | null, open?: {machineId: string; threadId: string}): Promise<void> {
		answered(
			await http.put<AgentEstimateKnobsResponse>(Endpoints.KONVO_AGENTS_TIME_ESTIMATE, {
				body: {knobs},
				mode: 'silent',
			}),
			'PUT',
		);
		// Every chat's estimate changes: what the side bar keeps is out of date.
		runInAction(() => {
			for (const [chatKey, chat] of this.chats) this.chats.set(chatKey, {...chat, lightAt: null});
		});
		if (open) await this.refresh(open.machineId, open.threadId);
	}

	/** What Konvo keeps for the chat, read again now (no machine). */
	private async refresh(machineId: string, threadId: string): Promise<void> {
		const response = answered(
			await http.get<AgentTimeView>(Endpoints.KONVO_AGENTS_THREAD_TIME(machineId, threadId), {
				query: {tz: timeZone()},
				mode: 'silent',
			}),
		);
		runInAction(() => this.put(machineId, threadId, {view: response.body, error: null}));
	}

	async loadOptions(): Promise<void> {
		if (this.optionsLoading) return;
		this.optionsLoading = true;
		try {
			const response = answered(
				await http.get<AgentTimeClientOptions>(Endpoints.KONVO_AGENTS_TIME_CLIENTS, {mode: 'silent'}),
			);
			runInAction(() => {
				this.options = response.body.options;
			});
		} catch {
			runInAction(() => {
				this.options ??= [];
			});
		} finally {
			runInAction(() => {
				this.optionsLoading = false;
			});
		}
	}

	reset(): void {
		this.chats.clear();
		this.options = null;
	}
}

export default new AgentTimeStore();
